// The earnings of a payslip exactly as payroll stored them. Nothing here is recalculated: gross is the stored gross,
// and each line is a stored column. Base pay is stored after the deduction for absences and unpaid leave.

import { shortDate } from "../../../components/calendar/calendarDays";
import { HOLIDAY_PAY_NOTE } from "./holidayPayView";

export const HOLIDAY_PRACTICE_NOTE = "Paid under your employer's holiday pay practice (not required by law for this business).";

const cents = (value) => Number(value || 0);

/** "20 Mar" */
const holidayDate = (date) => shortDate(String(date).slice(0, 10), { year: false });

/** "Holiday pay — Eid'l Fitr, 20 Mar (worked)": the final amount of each paid holiday day, nothing about how it was decided. */
function holidayDays(payslip) {
  const rows = Array.isArray(payslip.holiday_lines) ? payslip.holiday_lines : [];
  return rows
    .filter((row) => cents(row.amountCents) > 0 && row.date)
    .map((row) => ({
      key: `holiday-${row.date}`,
      label: `Holiday pay — ${row.name || "Holiday"}, ${holidayDate(row.date)} (${row.worked ? "worked" : "not worked"})`,
      cents: cents(row.amountCents),
      // Report 58 (M1): a day paid only because the business pays more than the law requires says so.
      note: row.paidByBusinessPractice ? HOLIDAY_PRACTICE_NOTE : undefined,
    }));
}

/** "Allowance — Meal": the named allowance items payroll stored with this payslip. */
function allowanceItems(payslip) {
  const rows = Array.isArray(payslip.earning_lines) ? payslip.earning_lines : [];
  return rows
    .filter((row) => (row.type || "allowance") === "allowance" && row.itemised && row.label && cents(row.amount_cents) > 0)
    .map((row, index) => ({ key: `allowance-${row.id ?? index}`, label: `Allowance — ${row.label}`, cents: cents(row.amount_cents) }));
}

export function payslipEarnings(payslip) {
  if (!payslip) return { lines: [], grossCents: 0, addsUp: true };
  const lines = [{ key: "base", label: "Base pay", cents: cents(payslip.base_salary_cents), absenceCents: cents(payslip.leave_deduction_cents) }];
  if (cents(payslip.allowances_cents) > 0) {
    // APP-HR-103: the allowance items stored with the payslip, one line each — but only when they add up to the
    // stored allowance exactly. A payslip from before the items were kept, or a pay record that named none, shows the
    // one stored total. No item is ever made up here.
    const items = allowanceItems(payslip);
    if (items.length && items.reduce((sum, item) => sum + item.cents, 0) === cents(payslip.allowances_cents)) lines.push(...items);
    else lines.push({ key: "allowance", label: "Fixed allowance", cents: cents(payslip.allowances_cents) });
  }
  if (cents(payslip.overtime_pay_cents) > 0) {
    lines.push({ key: "overtime", label: "Overtime pay", cents: cents(payslip.overtime_pay_cents), minutes: cents(payslip.overtime_minutes) });
  }
  if (cents(payslip.holiday_pay_cents) > 0) {
    // Holiday Calendar Phase C: one line per paid holiday day when payroll stored them, and only when those days add
    // up to the stored holiday pay exactly. Otherwise (an older payslip) the single stored total is shown, so the
    // money is never listed twice or left unexplained.
    const days = holidayDays(payslip);
    if (days.length && days.reduce((sum, day) => sum + day.cents, 0) === cents(payslip.holiday_pay_cents)) lines.push(...days);
    else lines.push({ key: "holiday", label: "Holiday pay", cents: cents(payslip.holiday_pay_cents), note: HOLIDAY_PAY_NOTE });
  }
  if (cents(payslip.premium_pay_cents) > 0) {
    // Report 55: rest-day pay and night shift differential, one line per date from the server's stored working, only
    // when those lines add up to the stored premium exactly; otherwise the one stored total.
    const stored = (payslip.calculation?.earnings?.lines || [])
      .filter((line) => ["rest_day_pay", "night_differential"].includes(line.code) && cents(line.amountCents) > 0)
      .map((line, index) => ({ key: `premium-${line.code}-${index}`, label: line.label, cents: cents(line.amountCents), note: line.formula }));
    if (stored.length && stored.reduce((sum, line) => sum + line.cents, 0) === cents(payslip.premium_pay_cents)) lines.push(...stored);
    else lines.push({ key: "premium", label: "Rest-day and night pay", cents: cents(payslip.premium_pay_cents) });
  }
  const grossCents = cents(payslip.gross_pay_cents);
  return { lines, grossCents, addsUp: lines.reduce((sum, line) => sum + line.cents, 0) === grossCents };
}
